export type LpProductRequest = {
  readonly keyword?: string;
  readonly sort?: string;
  readonly pageNo?: number;
  readonly pageSize?: number;
  readonly sq?: string;
  readonly bcds?: readonly number[];
  readonly shopCds?: readonly string[];
  readonly mcd?: string;
  readonly ccds?: readonly string[];
  readonly pcds?: readonly string[];
  readonly lcds?: readonly string[];
  readonly status?: readonly string[];
  readonly minPrice?: number;
  readonly maxPrice?: number;
  readonly colors?: readonly string[];
  readonly styles?: readonly string[];
  readonly tpos?: readonly string[];
  readonly todayDelivery?: boolean;
  readonly freeDelivery?: boolean;
  readonly sale?: boolean;
  readonly exclusive?: boolean;
  readonly celeb?: boolean;
  readonly excludeOutOfStock?: boolean;
  readonly excludePreOrder?: boolean;
  readonly products?: readonly string[];
  readonly saleTags?: readonly string[];
  readonly dcRateGroup?: readonly string[];
  readonly filters?: readonly string[];
  readonly domainType?: string;
  readonly source?: string;
  readonly device?: string;
  readonly searchType?: string;
};

/** 신규 LP 6개 도메인이 공유하는 차원별 집계 type 값이다. */
export const LP_PRODUCT_FILTER_TYPES = [
  'category',
  'colors',
  'styles',
  'tpos',
  'price',
  'brand',
  'top-brand',
  'new-brand',
  'products',
] as const;

export type LpProductFilterType = (typeof LP_PRODUCT_FILTER_TYPES)[number];

export type LpSaleProductFilterType = LpProductFilterType | 'dc-rate-group';

export interface LpProductFilterOption<TCode extends string | number = string> {
  readonly code: TCode;
  readonly name: string;
  readonly count: number;
  readonly engName?: string;
  readonly hex?: string;
}

export const getUniqueLpProductFilterOptions = <TCode extends string | number>(
  options: readonly LpProductFilterOption<TCode>[] | null | undefined
): readonly LpProductFilterOption<TCode>[] | null | undefined => {
  if (!options) return options;

  const codes = new Set<TCode>();

  return options.filter(option => {
    if (codes.has(option.code)) return false;

    codes.add(option.code);
    return true;
  });
};

export interface LpProductCategoryFilter {
  readonly count: number;
  readonly buckets: readonly LpProductFilterOption[];
}

export interface LpProductPriceRange {
  readonly min: number;
  readonly max: number;
  readonly limit: number;
  readonly count: number;
}

export interface LpProductGroups {
  readonly productType?: readonly LpProductFilterOption[];
  readonly benefit?: readonly LpProductFilterOption[];
}

export type LpProductGroupType = keyof LpProductGroups;

export interface LpProductFilterNameOption<
  TCode extends string | number = string
> {
  readonly code: TCode;
  readonly name: string;
  readonly engName?: string;
}

/** init·filter 응답의 data.filters 원본 구조다. */
export interface LpProductFilters {
  readonly category?: LpProductCategoryFilter | null;
  readonly rootCategory?: readonly LpProductFilterOption[] | null;
  readonly colors?: readonly LpProductFilterOption[] | null;
  readonly styles?: readonly LpProductFilterOption[] | null;
  readonly tpos?: readonly LpProductFilterOption[] | null;
  readonly priceRange?: LpProductPriceRange | null;
  readonly brand?: readonly LpProductFilterOption<number>[] | null;
  readonly brands?: readonly LpProductFilterOption<number>[] | null;
  readonly topBrand?: readonly LpProductFilterOption<number>[] | null;
  readonly newBrand?: readonly LpProductFilterOption<number>[] | null;
  readonly products?: LpProductGroups | null;
  readonly saleTags?: readonly LpProductFilterOption[] | null;
  readonly dcRateGroup?: readonly LpProductFilterOption[] | null;
}

export const getLpProductBrandOptions = (
  filters: Pick<LpProductFilters, 'brand' | 'brands'>
): LpProductFilters['brand'] =>
  getUniqueLpProductFilterOptions(filters.brands ?? filters.brand);

export interface LpProductFilterNames {
  readonly brand?: readonly LpProductFilterNameOption<number>[] | null;
  readonly colors?: readonly LpProductFilterNameOption[] | null;
  readonly category?: readonly LpProductFilterNameOption[] | null;
  readonly styles?: readonly LpProductFilterNameOption[] | null;
  readonly tpos?: readonly LpProductFilterNameOption[] | null;
  readonly todayDelivery?: LpProductFilterNameOption | null;
  readonly freeDelivery?: LpProductFilterNameOption | null;
  readonly sale?: LpProductFilterNameOption | null;
  readonly exclusive?: LpProductFilterNameOption | null;
  readonly celeb?: LpProductFilterNameOption | null;
  readonly excludeOutOfStock?: LpProductFilterNameOption | null;
  readonly excludePreOrder?: LpProductFilterNameOption | null;
}

export const getLpProductGroupTypeByCode = (
  products: LpProductGroups | null | undefined,
  code: string
): LpProductGroupType | undefined => {
  const groupByCode = new Map<string, LpProductGroupType>([
    ...(products?.productType ?? []).map(
      option => [String(option.code), 'productType'] as const
    ),
    ...(products?.benefit ?? []).map(
      option => [String(option.code), 'benefit'] as const
    ),
  ]);

  return groupByCode.get(code);
};

export interface LpProductSort {
  readonly code: string;
  readonly name: string;
}

export interface LpProductSortInfo {
  readonly code: string;
  readonly defaultValue: string;
  readonly sort: LpProductSort[];
}

export interface LpProductInitResult {
  readonly total: number;
  readonly sortInfo: LpProductSortInfo[];
  readonly filterName: LpProductFilterNames;
  readonly filters: LpProductFilters;
}

export interface LpProductFilterResult {
  readonly total: number;
  readonly filters: LpProductFilters;
}

export interface LpProductCountResult {
  readonly total: number;
}

export interface LpCategoryPath {
  readonly mediumCd: string;
  readonly largeCategory: string;
  readonly middleCategory?: string;
  readonly smallCategory?: string;
}

export interface LpSearchProductRequest extends LpProductRequest {
  readonly keyword: string;
}
